import React, { useMemo } from 'react';
import styled from 'styled-components';

import { MethodPayment, Methods } from '~/api/finance/financeApiTypes';
import { choosePaySelector } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.selector';
import { choosePayActions } from '~/feature/finance/BuyingSteps/ChoosePay/choosePay.slice';
import { BalanceMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/BalanceMethodPayment/BalanceMethodPayment';
import {
  BankCardMethodPayment,
  BankCardMethodPaymentPublicProps,
} from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/BankCardMethodPayment/BankCardMethodPayment';
import { MethodPaymentSkeleton } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/MethodPaymentSkeleton';
import { QiwiMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/QiwiMethodPayment';
import { RobokassaMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/RobokassaMethodPayment/RobokassaMethodPayment';
import { SaveBankCardMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/SaveBankCardMethodPayment/SaveBankCardMethodPayment';
import { SberPayMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/SberPayMethodPayment';
import { SBPMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/SBPMethodPayment';
import { TinkoffMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/TinkoffMethodPayment';
import { YandexMoneyMethodPayment } from '~/feature/finance/BuyingSteps/ChoosePay/MethodPayments/YandexMoneyMethodPayment';
import { lessThan } from '~/lib/mediaQuery';
import { useAppDispatch, useAppSelector } from '~/store';

export interface MethodPaymentsListPublicProps {
  showMethods?: Methods[];
}

interface MethodPaymentsListProps extends BankCardMethodPaymentPublicProps, MethodPaymentsListPublicProps {
  price: number;
  changeActiveMethod: () => Promise<void>;
  isAvailablePayBox?: boolean;
  isPayLoading?: boolean;
}

export const MethodPaymentsList = ({
  price,
  changeActiveMethod,
  isAvailablePayBox = true,
  isPayLoading,
  isShowSaveCardConfirm,
  showMethods,
}: MethodPaymentsListProps) => {
  const dispatch = useAppDispatch();
  const { methods, isLoading } = useAppSelector(choosePaySelector);

  const changeActiveMethodHandler = async (method: MethodPayment) => {
    dispatch(choosePayActions.changeActiveMethod(method));
    dispatch(choosePayActions.setIsPayLoading(true));
    try {
      await changeActiveMethod();
    } finally {
      dispatch(choosePayActions.setIsPayLoading(false));
    }
  };

  const changedOrderMethods = useMemo(() => {
    const filteredMethods = showMethods
      ? methods.filter((m) => showMethods.includes(m.type))
      : methods;
    const RobokassaMethod = filteredMethods.find((m) => m.type === Methods.Robokassa);

    if (RobokassaMethod) {
      return filteredMethods.filter((m) => m.type !== Methods.Robokassa).reduce((acc, m) => {
        acc.push(m);

        if (m.type === Methods.bankCard) {
          acc.push(RobokassaMethod);
        }

        return acc;
      }, []);
    }

    return filteredMethods;
  }, [methods]);

  if (isLoading) {
    return (
      <SCMethodPaymentsList>
        <MethodPaymentSkeleton />
        <MethodPaymentSkeleton />
        <MethodPaymentSkeleton />
        <MethodPaymentSkeleton />
        <MethodPaymentSkeleton />
        <MethodPaymentSkeleton />
      </SCMethodPaymentsList>
    );
  }

  return (
    <SCMethodPaymentsList>
      {changedOrderMethods.map((method) => {
        switch (method.type) {
          case Methods.balance:
            return (
              <BalanceMethodPayment
                price={price}
                method={method}
                onCheck={changeActiveMethodHandler}
                isPayLoading={isPayLoading}
                key={method.name}
              />
            );
          case Methods.savedBankCard:
            return (
              <SaveBankCardMethodPayment
                method={method}
                onCheck={changeActiveMethodHandler}
                isPayLoading={isPayLoading}
                key={method.name}
              />
            );
          case Methods.bankCard:
            return (
              <BankCardMethodPayment
                method={method}
                onCheck={changeActiveMethodHandler}
                isPayLoading={isPayLoading}
                key={method.name}
                isShowSaveCardConfirm={isShowSaveCardConfirm}
              />
            );
          case Methods.Robokassa:
            return (
              <RobokassaMethodPayment
                method={method}
                onCheck={changeActiveMethodHandler}
                isPayLoading={isPayLoading}
                key={method.name}
              />
            );
          case Methods.Qiwi:
            return (
              <QiwiMethodPayment
                method={method}
                onCheck={changeActiveMethodHandler}
                isPayLoading={isPayLoading}
                key={method.name}
              />
            );
          case Methods.yoo_money:
            return (
              <YandexMoneyMethodPayment
                method={method}
                onCheck={changeActiveMethodHandler}
                isPayLoading={isPayLoading}
                key={method.name}
              />
            );
          case Methods.SBP:
            return (
              <SBPMethodPayment
                method={method}
                onCheck={changeActiveMethodHandler}
                isPayLoading={isPayLoading}
                key={method.name}
              />
            );
          case Methods.Tinkoff:
            return (
              <>
                <TinkoffMethodPayment
                  method={method}
                  onCheck={changeActiveMethodHandler}
                  isHide={!isAvailablePayBox}
                  isPayLoading={isPayLoading}
                  key={method.name}
                />
                <SberPayMethodPayment
                  method={method}
                  onCheck={changeActiveMethodHandler}
                  isHide={!isAvailablePayBox}
                  isPayLoading={isPayLoading}
                  key={`${method.name}sber`}
                />
              </>
            );
          default:
            return <></>;
        }
      })}
    </SCMethodPaymentsList>
  );
};

const SCMethodPaymentsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  justify-content: center;
  grid-column-gap: 16px;
  grid-row-gap: 22px;
  width: 100%;

  ${lessThan('sm')} {
    grid-column-gap: 12px;
    grid-row-gap: 16px;
  }

  ${lessThan('xs')} {
    grid-template-columns: 1fr 1fr;
  }
`;
